import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Home from './components/Home';
import Login from './components/Login';
import Profile from './components/Profile';
import Discovery from './components/Discovery';
import Matches from './components/Matches';
import Chat from './components/Chat';
import Subscription from './components/Subscription';
import WhoLikedYou from './components/WhoLikedYou';
import Friends from './components/Friends';
import AdminLogin from './components/AdminLogin';
import AdminPanel from './components/AdminPanel';
import FaceVerification from './components/FaceVerification';
import useSessionTimeout from './hooks/useSessionTimeout';
import './App.css';
import Blog from './components/Blog';
import Footer from './components/Footer';
import LegalPages from './components/LegalPages';
import CustomNotification, { useCustomNotifications } from './components/CustomNotification';
import GeoGate from "./components/GeoGate";
import SplashScreen from './components/SplashScreen';
import VirtualGifts from './components/VirtualGifts';
import GiftsDemo from './components/GiftsDemo';
import EnhancedChat from './components/EnhancedChat';

// Main App Content Component
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const { notification, showNotification, closeNotification } = useCustomNotifications();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [showHome, setShowHome] = useState(true); // Show home page by default
  
  // Admin route state
  const [isAdminRoute, setIsAdminRoute] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  // Get current page from URL
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/') return 'profile';
    if (path === '/discovery') return 'discovery';
    if (path === '/matches') return 'matches';
    if (path === '/likes') return 'whoLikedYou';
    if (path === '/friends') return 'friends';
    if (path === '/subscription') return 'subscription';
    if (path === '/gifts') return 'gifts';
    if (path === '/verify-face') return 'verification';
    if (path === '/blog') return 'blog';
    if (path === '/chat') return 'chat';
    if (path === '/terms') return 'terms';
    if (path === '/privacy') return 'privacy';
    if (path === '/safety') return 'safety';
    if (path === '/community') return 'community';
    if (path === '/about') return 'about';
    if (path === '/admin') return 'admin';
    return 'profile';
  };

  const currentPage = getCurrentPage();
  
  function App() {
  return (
    <GeoGate>
      {/* existing app: navigation, routes, etc. */}
      {/* Profile, Discover, Likes, Matches, Premium, Blog */}
    </GeoGate>
  );
}
  // Add this function to handle mobile menu
  const handleMobileMenuClick = (page) => {
    const routes = {
      'profile': '/',
      'discovery': '/discovery',
      'matches': '/matches',
      'whoLikedYou': '/likes',
      'friends': '/friends',
      'subscription': '/subscription',
      'gifts': '/gifts',
      'blog': '/blog'
    };
    navigate(routes[page] || '/');
    setShowMobileMenu(false);
  };

  // Initialize session timeout (30 minutes of inactivity)
  const { extendSession } = useSessionTimeout(30);

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);
      setLoading(false);
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user || null);
      
      if (session?.user) {
        extendSession();
      }
    });

    return () => subscription.unsubscribe();
  }, [extendSession]);

  // Add route detection
  useEffect(() => {
    const path = location.pathname;
    if (path === '/admin' || path.startsWith('/admin')) {
      setIsAdminRoute(true);
    } else {
      setIsAdminRoute(false);
    }
  }, [location.pathname]);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Message notification polling
  useEffect(() => {
    if (!user) return;
    
    let pollInterval;
    let lastCheckedTime = new Date().toISOString();
    
    const checkForNewMessages = async () => {
      try {
        const { data: newMessages, error } = await supabase
          .from('messages')
          .select('*')
          .eq('receiver_id', user.id)
          .gt('created_at', lastCheckedTime)
          .order('created_at', { ascending: false });
        
        if (error) return;
        
        if (newMessages && newMessages.length > 0) {
          // Get list of users who can message this user (matches + friends)
          const [matchesResult, friendsResult] = await Promise.all([
            // Check matches
            supabase
              .from('matches')
              .select('user_id, matched_user_id')
              .or(`user_id.eq.${user.id},matched_user_id.eq.${user.id}`),
            // Check friends
            supabase
              .from('friendships')
              .select('requester_id, addressee_id')
              .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`)
              .eq('status', 'accepted')
          ]);

          const allowedSenders = new Set();
          
          // Add matched users
          if (matchesResult.data) {
            matchesResult.data.forEach(match => {
              const otherUserId = match.user_id === user.id ? match.matched_user_id : match.user_id;
              allowedSenders.add(otherUserId);
            });
          }
          
          // Add friends
          if (friendsResult.data) {
            friendsResult.data.forEach(friendship => {
              const friendId = friendship.requester_id === user.id 
                ? friendship.addressee_id 
                : friendship.requester_id;
              allowedSenders.add(friendId);
            });
          }

          // Only show notifications for messages from allowed senders
          newMessages.forEach(async (message) => {
            if (allowedSenders.has(message.sender_id)) {
              // Only add notifications if not currently in chat with sender
              if (currentPage !== 'chat' || selectedMatch?.id !== message.sender_id) {
                setNotifications(prev => [...prev, message]);
                setHasNewMessages(true);
                
                // Get sender name for better notification
                const { data: senderProfile } = await supabase
                  .from('profiles')
                  .select('name')
                  .eq('id', message.sender_id)
                  .single();

                // Show custom notification
                showNotification({
                  title: `New Message from ${senderProfile?.name || 'Someone'}`,
                  body: message.content,
                  avatar: '💬',
                  senderId: message.sender_id
                });
              }
            }
          });
          
          lastCheckedTime = newMessages[0].created_at;
        }
      } catch (error) {
        console.error('Error checking messages:', error);
      }
    };
    
    // Check immediately, then every 5 seconds
    checkForNewMessages();
    pollInterval = setInterval(checkForNewMessages, 5000);
    
    return () => clearInterval(pollInterval);
  }, [user, currentPage, selectedMatch]);

  // Enhanced session timeout with custom warning
  useEffect(() => {
    if (!user) return;

    let timeoutId;
    let warningTimeoutId;
    let countdownInterval;

    const resetInactivityTimer = () => {
      // Clear existing timers
      clearTimeout(timeoutId);
      clearTimeout(warningTimeoutId);
      clearInterval(countdownInterval);
      setShowSessionWarning(false);
      setSessionTimeRemaining(null);

      // Set warning timer (25 minutes - show warning 5 minutes before logout)
      warningTimeoutId = setTimeout(() => {
        setShowSessionWarning(true);
        setSessionTimeRemaining(300); // 5 minutes = 300 seconds

        // Start countdown
        countdownInterval = setInterval(() => {
          setSessionTimeRemaining(prev => {
            if (prev <= 1) {
              handleAutoLogout();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, 25 * 60 * 1000); // 25 minutes

      // Set auto-logout timer (30 minutes)
      timeoutId = setTimeout(() => {
        handleAutoLogout();
      }, 30 * 60 * 1000); // 30 minutes
    };

    const handleAutoLogout = async () => {
      try {
        await supabase.auth.signOut();
        alert('You have been automatically logged out due to inactivity.');
      } catch (error) {
        console.error('Error during auto-logout:', error);
      }
    };

    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Activity events to monitor
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initialize timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      clearTimeout(timeoutId);
      clearTimeout(warningTimeoutId);
      clearInterval(countdownInterval);
    };
  }, [user]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleStayLoggedIn = () => {
    setShowSessionWarning(false);
    setSessionTimeRemaining(null);
    extendSession();
  };

  const handleLogoutNow = () => {
    handleLogout();
  };

  const handleAdminLogin = (adminData) => {
    setAdminUser(adminData);
    navigate('/admin');
  };

  // Function to open chat with a matched user
  const openChat = (matchedUser) => {
    setSelectedMatch(matchedUser);
    navigate('/chat');
    // Clear notifications when opening chat
    setNotifications([]);
    setHasNewMessages(false);
  };

  // Function to go back from chat
  const backFromChat = () => {
    setSelectedMatch(null);
    navigate('/matches');
    // Force a small delay to ensure the matches component refreshes unread counts
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('refreshUnreadCounts'));
    }, 100);
  };

  // Function to navigate to subscription page
  const goToSubscription = () => {
    navigate('/subscription');
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px', color: 'white' }}>Loading...</div>;
  }

  return (
    <div className="App">
      {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
      <Routes>
        {/* Admin Routes */}
        <Route path="/admin" element={
          adminUser ? (
            <AdminPanel user={user || { id: adminUser.user_id }} adminUser={adminUser} adminLevel={adminUser.admin_level} />
          ) : (
            <AdminLogin onAdminLogin={handleAdminLogin} />
          )
        } />

        {/* Public Routes */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<Blog />} />
        <Route path="/gifts-demo" element={<GiftsDemo />} />
        <Route path="/terms" element={<LegalPages activePage="terms" />} />
        <Route path="/privacy" element={<LegalPages activePage="privacy" />} />
        <Route path="/safety" element={<LegalPages activePage="safety" />} />
        <Route path="/community" element={<LegalPages activePage="community" />} />
        <Route path="/about" element={<LegalPages activePage="about" />} />

        {/* Protected Routes */}
        <Route path="/*" element={
          user ? (
            <div>
              {/* Session Warning Modal */}
              {showSessionWarning && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1000
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    borderRadius: '20px',
                    padding: '30px',
                    maxWidth: '400px',
                    textAlign: 'center',
                    border: '2px solid #ff6b6b',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏰</div>
                    <h3 style={{ 
                      color: '#ff6b6b', 
                      marginBottom: '15px',
                      fontSize: '20px'
                    }}>
                      Session Expiring Soon
                    </h3>
                    <p style={{ 
                      color: 'rgba(255, 255, 255, 0.8)',
                      marginBottom: '20px',
                      lineHeight: '1.5'
                    }}>
                      Your session will expire in{' '}
                      <span style={{ 
                        color: '#ff6b6b', 
                        fontWeight: '600',
                        fontSize: '18px' 
                      }}>
                        {formatTime(sessionTimeRemaining)}
                      </span>
                      {' '}due to inactivity.
                    </p>
                    <div style={{ 
                      display: 'flex', 
                      gap: '15px',
                      justifyContent: 'center'
                    }}>
                      <button
                        onClick={handleStayLoggedIn}
                        style={{
                          background: 'linear-gradient(90deg, #51cf66, #40c057)',
                          color: 'white',
                          border: 'none',
                          padding: '12px 20px',
                          borderRadius: '10px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease'
                        }}
                        onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                      >
                        Stay Logged In
                      </button>
                      <button
                        onClick={handleLogoutNow}
                        style={{
                          background: 'linear-gradient(90deg, #ff6b6b, #ee5a52)',
                          color: 'white',
                          border: 'none',
                          padding: '12px 20px',
                          borderRadius: '10px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease'
                        }}
                        onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                      >
                        Logout Now
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="nav-header">
                {/* Mobile hamburger button */}
                <button 
                  className="mobile-menu-button"
                  onClick={() => setShowMobileMenu(true)}
                >
                  ☰
                </button>
                
                {/* Desktop navigation */}
                <div>
                  <button 
                    onClick={() => navigate('/')}
                    className={currentPage === 'profile' ? 'nav-button' : 'nav-button inactive'}
                    style={{ marginRight: '10px' }}
                  >
                    Profile
                  </button>
                  <button 
                    onClick={() => navigate('/discovery')}
                    className={currentPage === 'discovery' ? 'nav-button' : 'nav-button inactive'}
                    style={{ marginRight: '10px' }}
                  >
                    Discover
                  </button>
                  <button 
                    onClick={() => navigate('/likes')}
                    className={currentPage === 'whoLikedYou' ? 'nav-button' : 'nav-button inactive'}
                    style={{ marginRight: '10px' }}
                  >
                    👀 Likes
                  </button>
                  <button 
                    onClick={() => navigate('/friends')}
                    className={currentPage === 'friends' ? 'nav-button' : 'nav-button inactive'}
                    style={{ marginRight: '10px' }}
                  >
                    👥 Friends
                  </button>
                  <button 
                    onClick={() => navigate('/matches')}
                    className={currentPage === 'matches' ? 'nav-button' : 'nav-button inactive'}
                    style={{ marginRight: '10px', position: 'relative' }}
                  >
                    Matches
                    {hasNewMessages && (
                      <span style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        background: '#ff4757',
                        color: 'white',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {notifications.length}
                      </span>
                    )}
                  </button>
                  <button 
                    onClick={() => navigate('/subscription')}
                    className={currentPage === 'subscription' ? 'nav-button' : 'nav-button inactive'}
                    style={{ marginRight: '10px' }}
                  >
                    💎 Premium
                  </button>
                  <button 
                    onClick={() => navigate('/gifts')}
                    className={currentPage === 'gifts' ? 'nav-button' : 'nav-button inactive'}
                    style={{ marginRight: '10px' }}
                  >
                    🎁 Gifts
                  </button>
                  <button 
                    onClick={() => navigate('/blog')}
                    className={currentPage === 'blog' ? 'nav-button' : 'nav-button inactive'}
                    style={{ marginRight: '10px' }}
                  >
                    📝 Blog
                  </button>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  {/* Session status indicator */}
                  <div style={{
                    padding: '4px 8px',
                    background: 'rgba(0, 233, 255, 0.2)',
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 233, 255, 0.4)',
                    fontSize: '11px',
                    color: '#00E9FF'
                  }}>
                    🔒 Session Active
                  </div>
                  
                  <button onClick={handleLogout} className="nav-button">
                    Logout
                  </button>
                </div>
              </div>

              {/* Mobile Menu Overlay */}
              {showMobileMenu && (
                <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)}>
                  <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
                    <button 
                      className="mobile-menu-close"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      ✕
                    </button>
                    
                    <button 
                      onClick={() => handleMobileMenuClick('profile')}
                      className={currentPage === 'profile' ? 'nav-button' : 'nav-button inactive'}
                    >
                      👤 Profile
                    </button>
                    
                    <button 
                      onClick={() => handleMobileMenuClick('discovery')}
                      className={currentPage === 'discovery' ? 'nav-button' : 'nav-button inactive'}
                    >
                      🔍 Discover
                    </button>
                    
                    <button 
                      onClick={() => handleMobileMenuClick('whoLikedYou')}
                      className={currentPage === 'whoLikedYou' ? 'nav-button' : 'nav-button inactive'}
                    >
                      👀 Likes
                    </button>
                    
                    <button 
                      onClick={() => handleMobileMenuClick('friends')}
                      className={currentPage === 'friends' ? 'nav-button' : 'nav-button inactive'}
                    >
                      👥 Friends
                    </button>
                    
                    <button 
                      onClick={() => handleMobileMenuClick('matches')}
                      className={currentPage === 'matches' ? 'nav-button' : 'nav-button inactive'}
                      style={{ position: 'relative' }}
                    >
                      💬 Matches
                      {hasNewMessages && (
                        <span style={{
                          position: 'absolute',
                          top: '8px',
                          right: '15px',
                          background: '#ff4757',
                          color: 'white',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {notifications.length}
                        </span>
                      )}
                    </button>
                    
                    <button 
                      onClick={() => handleMobileMenuClick('subscription')}
                      className={currentPage === 'subscription' ? 'nav-button' : 'nav-button inactive'}
                    >
                      💎 Premium
                    </button>
                    
                    <button 
                      onClick={() => handleMobileMenuClick('gifts')}
                      className={currentPage === 'gifts' ? 'nav-button' : 'nav-button inactive'}
                    >
                      🎁 Gifts
                    </button>
                    
                    <button 
                      onClick={() => handleMobileMenuClick('blog')}
                      className={currentPage === 'blog' ? 'nav-button' : 'nav-button inactive'}
                    >
                      📝 Blog
                    </button>
                    
                    <div style={{ 
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)', 
                      marginTop: '20px', 
                      paddingTop: '20px' 
                    }}>
                      <button onClick={handleLogout} className="nav-button" style={{ width: '100%' }}>
                        🚪 Logout
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Route Components */}
              <Routes>
                <Route path="/" element={<Profile />} />
                <Route path="/discovery" element={<Discovery onChatOpen={openChat} />} />
                <Route path="/likes" element={<WhoLikedYou user={user} onUpgradeClick={goToSubscription} />} />
                <Route path="/friends" element={<Friends onChatOpen={openChat} />} />
                <Route path="/matches" element={<Matches onChatOpen={openChat} />} />
                <Route path="/subscription" element={<Subscription user={user} />} />
                <Route path="/verify-face" element={<FaceVerification />} />
                <Route path="/gifts" element={<VirtualGifts userType="premium" />} />
                <Route path="/chat" element={
                  selectedMatch ? (
                    <EnhancedChat 
                      user={user} 
                      matchedUser={selectedMatch} 
                      onBack={backFromChat} 
                    />
                  ) : (
                    <Navigate to="/matches" replace />
                  )
                } />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          ) : (
            showHome ? (
              <Home onShowLogin={() => setShowHome(false)} />
            ) : (
              <Login onShowHome={() => setShowHome(true)} />
            )
          )
        } />
      </Routes>
      
      <Footer />
      
      {/* Custom Notification */}
      <CustomNotification 
        notification={notification}
        onClose={closeNotification}
        onNavigate={() => {
          // Navigate to chat when notification is clicked
          if (notification?.senderId) {
            // Find the sender and open chat
            // You can enhance this based on your needs
          }
        }}
      />
    </div>
  );
}

// Main App Component with Router
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;